import React, { ReactElement, Suspense } from 'react'
import { Route, BrowserRouter, Routes } from 'react-router-dom'
import { ROUTES } from 'constants/Route.constants'
import { FullPageLoader } from 'components/Loaders'
import { conditionalLazy } from 'features/lazy-loader/ComponentLoader'
import { ContactAndChannelBrowsersFeatureFlagRedirect } from 'features/routing/FeatureFlagRedirects'
import LightboxProvider from 'features/lightbox/LightboxProvider'
import DefaultRedirect from 'features/routing/DefaultRedirect'
import NotificationsProvider from 'features/notifications/NotificationsProvider'

const AliceDocumentSigning = conditionalLazy(() => import('pages/AliceDocumentSigning'))
const AliceDocumentUpload = conditionalLazy(() => import('pages/AliceDocumentUpload'))
const AliceConditionCheck = conditionalLazy(() => import('pages/AliceConditionCheck'))
const AliceExperienceProvider = conditionalLazy(() => import('features/auth/AliceExperienceProvider'))
const AllProviders = conditionalLazy(() => import('features/providers/AllProviders'))
const ClientSideDataProvider = conditionalLazy(() => import('features/storage/Redux/ClientSideDataProvider'))
const ForgotPassword = conditionalLazy(() => import('features/auth/ForgotPassword'))
const LandingPageRedirect = conditionalLazy(() => import('pages/LandingPageRedirect'))
const PdfServiceWorkerProvider = conditionalLazy(() => import('features/providers/PdfServiceWorkerProvider'))
const ProductAnalyticsProvider = conditionalLazy(() => import('features/product-analytics/ProductAnalyticsProvider'))
const ServerSideDataProvider = conditionalLazy(() => import('features/storage/ReactQuery/ServerSideDataProvider'))
const SessionProvider = conditionalLazy(() => import('features/session-manager/SessionProvider'))
const SignInFlow = conditionalLazy(() => import('features/auth/SignInFlow'))
const SubscriptionSettingsAndConfirmationPage = conditionalLazy(
  () => import('pages/SubscriptionSettingsAndConfirmationPage')
)
const UnauthorizedPage = conditionalLazy(() => import('features/auth/UnauthorizedPage'))
const WorkspacePage = conditionalLazy(() => import('pages/WorkspacePage'))

export default function AppRouter(): ReactElement {
  return (
    <BrowserRouter>
      <Suspense fallback={<FullPageLoader data-testid="suspense-page-loader" />}>
        <Routes>
          <Route path={ROUTES.SUBSCRIPTION_CONFIRMATION} element={<SubscriptionSettingsAndConfirmationPage />} />
          <Route path={ROUTES.SUBSCRIPTION_SETTINGS} element={<SubscriptionSettingsAndConfirmationPage />} />
          <Route
            path={ROUTES.SIGN_IN}
            element={
              <AllProviders>
                <SessionProvider>
                  <SignInFlow />
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.FORGOT_PASSWORD}
            element={
              <AllProviders>
                <SessionProvider>
                  <ForgotPassword />
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={`${ROUTES.WORKSPACE_VENDOR_BROWSER(':envy')}`}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <ContactAndChannelBrowsersFeatureFlagRedirect>
                      <WorkspacePage />
                    </ContactAndChannelBrowsersFeatureFlagRedirect>
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={`${ROUTES.WORKSPACE_CHANNEL_BROWSER(':envy')}`}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <ContactAndChannelBrowsersFeatureFlagRedirect>
                      <WorkspacePage />
                    </ContactAndChannelBrowsersFeatureFlagRedirect>
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={`${ROUTES.WORKSPACE_SUPPORT(':envy')}`}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={`${ROUTES.WORKSPACE_INTERNAL_CHANNELS(':envy')}/:channelId`}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={`${ROUTES.WORKSPACE_EXTERNAL_CHANNELS(':envy')}/:channelId`}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_INTERNAL_CHANNELS(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_EXTERNAL_CHANNELS(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_CONTACTS(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_TEAM(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_RESPONSES(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_BROADCASTS(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_CONFIGURATIONS(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={`${ROUTES.WORKSPACE_SETTINGS(':envy')}/:tab`}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_PROFILE(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_USAGE_REPORT(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_CONTACT_GROUPS(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_GLOBAL_SEARCH(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_FEATURE_UPDATES(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_IMPORT_CONTACTS(':envy')}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <WorkspacePage />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_LANDING_REDIRECT}
            element={
              <ClientSideDataProvider>
                <ServerSideDataProvider>
                  <ProductAnalyticsProvider>
                    <NotificationsProvider>
                      <SessionProvider>
                        <LightboxProvider>
                          <LandingPageRedirect />
                        </LightboxProvider>
                      </SessionProvider>
                    </NotificationsProvider>
                  </ProductAnalyticsProvider>
                </ServerSideDataProvider>
              </ClientSideDataProvider>
            }
          />
          <Route
            path={ROUTES.WORKSPACE_DEPRECATED_EXTERNAL_CHANNELS}
            element={
              <AllProviders>
                <SessionProvider>
                  <LightboxProvider>
                    <LandingPageRedirect />
                  </LightboxProvider>
                </SessionProvider>
              </AllProviders>
            }
          />
          <Route
            path={`${ROUTES.DOCUMENT_UPLOAD}/:replyToken`}
            element={
              <ClientSideDataProvider>
                <ServerSideDataProvider>
                  <ProductAnalyticsProvider>
                    <NotificationsProvider>
                      <PdfServiceWorkerProvider>
                        <AliceExperienceProvider>
                          <AliceDocumentUpload />
                        </AliceExperienceProvider>
                      </PdfServiceWorkerProvider>
                    </NotificationsProvider>
                  </ProductAnalyticsProvider>
                </ServerSideDataProvider>
              </ClientSideDataProvider>
            }
          />
          <Route
            path={`${ROUTES.DOCUMENT_SIGNING}/:replyToken`}
            element={
              <ClientSideDataProvider>
                <ServerSideDataProvider>
                  <ProductAnalyticsProvider>
                    <NotificationsProvider>
                      <PdfServiceWorkerProvider>
                        <AliceExperienceProvider>
                          <AliceDocumentSigning />
                        </AliceExperienceProvider>
                      </PdfServiceWorkerProvider>
                    </NotificationsProvider>
                  </ProductAnalyticsProvider>
                </ServerSideDataProvider>
              </ClientSideDataProvider>
            }
          />
          <Route
            path={`${ROUTES.CONDITION_CHECK}/:replyToken`}
            element={
              <ClientSideDataProvider>
                <ServerSideDataProvider>
                  <ProductAnalyticsProvider>
                    <NotificationsProvider>
                      <AliceExperienceProvider>
                        <AliceConditionCheck />
                      </AliceExperienceProvider>
                    </NotificationsProvider>
                  </ProductAnalyticsProvider>
                </ServerSideDataProvider>
              </ClientSideDataProvider>
            }
          />
          <Route
            path={ROUTES.UNAUTHORIZED}
            element={
              <ServerSideDataProvider>
                <UnauthorizedPage />
              </ServerSideDataProvider>
            }
          />
          {/**
           This is the exact same as the fallback. We are making this explicit so that it is clear in code that
           this route exists for the purpose of redirecting to a channel after a user clicks on a desktop
           notification w/o an existing tab i.e. it opens a new tab at this route.
           */}
          <Route
            path={ROUTES.CHANNEL_REDIRECT}
            element={
              <ServerSideDataProvider>
                <ClientSideDataProvider>
                  <ProductAnalyticsProvider>
                    <DefaultRedirect />
                  </ProductAnalyticsProvider>
                </ClientSideDataProvider>
              </ServerSideDataProvider>
            }
          />
          <Route
            path="*"
            element={
              <ServerSideDataProvider>
                <ClientSideDataProvider>
                  <ProductAnalyticsProvider>
                    <DefaultRedirect />
                  </ProductAnalyticsProvider>
                </ClientSideDataProvider>
              </ServerSideDataProvider>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
